// dashboard/Dashboard.jsx
import { useEffect, useState } from "react";
import "./dashboard.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { 
  FiUsers, 
  FiUserCheck, 
  FiUserX, 
  FiTrendingUp, 
  FiActivity, 
  FiPieChart,
  FiCalendar,
  FiArrowUp,
  FiArrowDown,
  FiBell,
  FiClock,
  FiDollarSign,
  FiPercent,
  FiAward,
  FiZap,
  FiEye,
  FiEyeOff,
  FiRefreshCw,
  FiSettings,
  FiPlusCircle,
  FiDownload,
  FiMail,
  FiUserPlus,
  FiCreditCard,
  FiPackage,
  FiMessageCircle,
  FiStar,
  FiThumbsUp,
  FiHeart,
  FiBarChart2
} from 'react-icons/fi';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

export default function Dashboard({ darkMode }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [selectedPeriod, setSelectedPeriod] = useState('weekly');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_ENV === 'local' 
        ? process.env.REACT_APP_BACKEND_USER_API_LOCAL 
        : process.env.REACT_APP_BACKEND_USER_API;
      const res = await fetch(`${baseUrl}/api/users/dashboard-analytics`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
        },
      });
      if (!res.ok) throw new Error(`Fetch failed ${res.status}`);
      const json = await res.json();
      setAnalytics(json.data || null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch dashboard analytics', err);
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  };

  const chartData = analytics?.chartData || [];

  // Chart configurations with dark mode support
  const getChartColors = () => {
    return {
      textColor: darkMode ? '#f8fafc' : '#0f172a',
      gridColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
    };
  };

  // Bar Chart Data
  const barChartData = {
    labels: chartData.length > 0 ? chartData.map(item => item.label) : ['Active', 'Inactive', 'Pending', 'Expired'],
    datasets: [
      {
        label: 'Subscription Status',
        data: chartData.length > 0 ? chartData.map(item => item.value) : [13, 46, 8, 12],
        backgroundColor: ['#38bdf8', '#475569', '#f59e0b', '#10b981'],
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: darkMode ? 'rgba(17,24,39,0.9)' : 'rgba(255,255,255,0.9)',
        titleColor: darkMode ? '#f8fafc' : '#0f172a',
        bodyColor: darkMode ? '#94a3b8' : '#475569',
        borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        borderWidth: 1,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: getChartColors().gridColor,
        },
        ticks: {
          color: getChartColors().textColor,
          font: {
            size: 12,
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: getChartColors().textColor,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
    },
  };

  // Doughnut Chart Data
  const doughnutData = {
    labels: chartData.length > 0 ? chartData.map(item => item.label) : ['Active', 'Inactive', 'Pending'],
    datasets: [
      {
        data: chartData.length > 0 ? chartData.map(item => item.value) : [13, 46, 8],
        backgroundColor: ['#38bdf8', '#475569', '#f59e0b', '#10b981'],
        borderWidth: 0,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 12,
            weight: '500',
          },
          color: getChartColors().textColor,
        },
      },
    },
    cutout: '70%',
  };

  // Line Chart Data
  const lineData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'User Growth',
        data: [45, 52, 48, 58, 55, 62, 59, 65, 70, 68, 75, 82],
        borderColor: '#6366f1',
        backgroundColor: darkMode 
          ? 'rgba(99,102,241,0.1)' 
          : 'rgba(99,102,241,0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#6366f1',
        pointBorderColor: darkMode ? '#0b1120' : '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 4,
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: darkMode ? 'rgba(17,24,39,0.9)' : 'rgba(255,255,255,0.9)',
        titleColor: darkMode ? '#f8fafc' : '#0f172a',
        bodyColor: darkMode ? '#94a3b8' : '#475569',
        borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        borderWidth: 1,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: getChartColors().gridColor,
        },
        ticks: {
          color: getChartColors().textColor,
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: getChartColors().textColor,
        },
      },
    },
  };

  // Enhanced Stat Cards
  const statCards = [
    {
      title: 'Total Users',
      value: analytics?.totalUsers ?? 59,
      icon: <FiUsers size={22} />,
      color: '#38bdf8',
      bg: 'rgba(56, 189, 248, 0.12)',
      change: '+12.5%',
      trend: 'up',
      subtext: 'Active: 42'
    },
    {
      title: 'Active Subscriptions',
      value: analytics?.activeSubscriptions ?? 13,
      icon: <FiUserCheck size={22} />,
      color: '#10b981',
      bg: 'rgba(16, 185, 129, 0.12)',
      change: '+8.3%',
      trend: 'up',
      subtext: 'Renewal: 94%'
    },
    {
      title: 'Revenue',
      value: analytics?.revenue ?? '$12,450',
      icon: <FiDollarSign size={22} />,
      color: '#f59e0b',
      bg: 'rgba(245, 158, 11, 0.12)',
      change: '+23.1%',
      trend: 'up',
      subtext: 'Growth: +18%'
    },
    {
      title: 'Total Subscribers',
      value: analytics?.totalSubscribers ?? 156,
      icon: <FiUsers size={22} />,
      color: '#8b5cf6',
      bg: 'rgba(139, 92, 246, 0.12)',
      change: '+5.2%',
      trend: 'up',
      subtext: 'New: 24'
    },
  ];

  // Quick Actions
  const quickActions = [
    { icon: <FiUserPlus size={18} />, label: 'Add User', color: '#6366f1' },
    { icon: <FiPackage size={18} />, label: 'New Plan', color: '#10b981' },
    { icon: <FiPlusCircle size={18} />, label: 'Add Content', color: '#f59e0b' },
    { icon: <FiDownload size={18} />, label: 'Export Data', color: '#06b6d4' },
  ];

  // Recent Activity
  const recentActivities = [
    { icon: <FiUserPlus size={14} />, user: 'John Doe', action: 'registered', time: '2 min ago', color: '#6366f1' },
    { icon: <FiCreditCard size={14} />, user: 'Jane Smith', action: 'subscribed to Premium', time: '15 min ago', color: '#10b981' },
    { icon: <FiStar size={14} />, user: 'Mike Johnson', action: 'upgraded to Pro', time: '1 hour ago', color: '#f59e0b' },
    { icon: <FiMessageCircle size={14} />, user: 'Sarah Wilson', action: 'left a review', time: '3 hours ago', color: '#06b6d4' },
  ];

  // Top Performing Content
  const topContent = [
    { name: 'Hanuman Chalisa', category: 'Devotional', views: '2.4K', likes: '856', growth: '+12%' },
    { name: 'Morning Meditation', category: 'Meditation', views: '1.8K', likes: '623', growth: '+8%' },
    { name: 'Gayatri Mantra', category: 'Mantra', views: '1.5K', likes: '512', growth: '+15%' },
    { name: 'Krishna Bhajan', category: 'Bhajan', views: '1.2K', likes: '456', growth: '+6%' },
  ];

  return (
    <div className={`dashboard-container ${darkMode ? 'dark' : ''}`}>
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <h1 className="dashboard-greeting">Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 18 ? 'Afternoon' : 'Evening'} 👋</h1>
          <p className="dashboard-subtitle">Here's what's happening with your platform today</p>
        </div>
        <div className="dashboard-header-right">
          <div className="dashboard-date">
            <FiCalendar size={14} />
            <span>{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
          </div>
          <button className="refresh-btn" onClick={fetchAnalytics} disabled={loading}>
            <FiRefreshCw size={16} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {statCards.map((stat, index) => (
          <div key={index} className="stat-card modern">
            <div className="stat-card-header">
              <div className="stat-icon-wrapper" style={{ background: stat.bg, color: stat.color }}>
                {stat.icon}
              </div>
              <div className={`stat-change-badge ${stat.trend === 'up' ? 'positive' : 'negative'}`}>
                {stat.trend === 'up' ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />}
                {stat.change}
              </div>
            </div>
            <div className="stat-value">
              {loading ? '...' : typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
            </div>
            <div className="stat-title">{stat.title}</div>
            <div className="stat-footer">
              <span className="stat-subtext">{stat.subtext}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions Bar */}
      <div className="quick-actions-bar">
        <span className="quick-actions-label">⚡ Quick Actions</span>
        <div className="quick-actions-grid">
          {quickActions.map((action, index) => (
            <button key={index} className="quick-action-btn">
              <span className="quick-action-icon" style={{ color: action.color }}>
                {action.icon}
              </span>
              <span className="quick-action-label">{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Dashboard Grid */}
      <div className="dashboard-grid">
        {/* Left Column */}
        <div className="dashboard-col-left">
          {/* Bar Chart */}
          <div className="chart-card glass">
            <div className="chart-header">
              <div className="chart-title">
                <FiBarChart2 size={18} />
                <h6>Subscription Status</h6>
              </div>
              <div className="chart-actions">
                <button 
                  className={`chart-btn ${selectedPeriod === 'weekly' ? 'active' : ''}`}
                  onClick={() => setSelectedPeriod('weekly')}
                >
                  Weekly
                </button>
                <button 
                  className={`chart-btn ${selectedPeriod === 'monthly' ? 'active' : ''}`}
                  onClick={() => setSelectedPeriod('monthly')}
                >
                  Monthly
                </button>
              </div>
            </div>
            <div className="chart-wrapper">
              <Bar data={barChartData} options={barOptions} />
            </div>
          </div>

          {/* Top Content */}
          <div className="chart-card glass">
            <div className="chart-header">
              <div className="chart-title">
                <FiAward size={18} />
                <h6>Top Performing</h6>
              </div>
              <span className="chart-badge">Popular</span>
            </div>
            <div className="top-content-list">
              {topContent.map((item, index) => (
                <div key={index} className="top-content-item">
                  <div className="top-content-rank">#{index + 1}</div>
                  <div className="top-content-info">
                    <div className="top-content-name">{item.name}</div>
                    <div className="top-content-category">{item.category}</div>
                  </div>
                  <div className="top-content-stats">
                    <span className="top-content-views">{item.views}</span>
                    <span className="top-content-likes">❤️ {item.likes}</span>
                    <span className={`top-content-growth ${item.growth.startsWith('+') ? 'positive' : 'negative'}`}>
                      {item.growth}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="dashboard-col-right">
          {/* Doughnut Chart */}
          <div className="chart-card glass">
            <div className="chart-header">
              <div className="chart-title">
                <FiPieChart size={18} />
                <h6>Distribution</h6>
              </div>
              <span className="chart-badge">Overview</span>
            </div>
            <div className="chart-wrapper doughnut-wrapper">
              <Doughnut data={doughnutData} options={doughnutOptions} />
            </div>
          </div>

          {/* Recent Activity */}
          <div className="chart-card glass">
            <div className="chart-header">
              <div className="chart-title">
                <FiClock size={18} />
                <h6>Recent Activity</h6>
              </div>
              <button className="view-all-btn">View All</button>
            </div>
            <div className="activity-list">
              {recentActivities.map((activity, index) => (
                <div key={index} className="activity-item">
                  <div className="activity-icon" style={{ background: `${activity.color}15`, color: activity.color }}>
                    {activity.icon}
                  </div>
                  <div className="activity-content">
                    <span className="activity-text">
                      <strong>{activity.user}</strong> {activity.action}
                    </span>
                    <span className="activity-time">{activity.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Full Width Line Chart */}
      <div className="chart-card glass full-width">
        <div className="chart-header">
          <div className="chart-title">
            <FiTrendingUp size={18} />
            <h6>User Growth Trend</h6>
          </div>
          <div className="chart-actions">
            <button className="chart-btn active">Yearly</button>
            <button className="chart-btn">Monthly</button>
          </div>
        </div>
        <div className="chart-wrapper line-chart-wrapper">
          <Line data={lineData} options={lineOptions} />
        </div>
      </div>
    </div>
  );
}